import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { ErrorLike } from '@apollo/client';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import {
  FormCategory,
  FormTemplateCreateInputObject,
  FormTemplateVersionStatusEnum,
  Task,
  useDashboardTaskScheduleQuery,
  useFormCategoryCreateMutation,
  useTaskBulkSchedulingMutation,
} from 'graphql-common';
import { Option, Values } from '@lib/interfaces/form';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import removeIdFields from '@lib/utils/removeIdsFromObject';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import {
  UseBulkSelectResult,
  useTableItemsBulkSelect,
} from '@lib/hooks/useTableItemsBulkSelect';
import useLoadingState from '@lib/hooks/useLoadingState';
import useFormTemplateTasksQueryHook, {
  FormTemplateTasksQueryHookResult,
} from 'utils/fetch/hooks/useFormTemplateTasksQueryHook';
import { ModalManagementResult } from 'routes/FormTemplate/utils/useModalManagement';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import useFilterParams from '@lib/hooks/useFilterParams';
import { GetUrlParams } from '@lib/enums/urls';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import getAllTableItemsIds from '@lib/utils/getAllTableItemsIds';
import { NEW } from '@lib/enums/common';
import useTemplateVersionMutations from 'routes/FormTemplate/utils/useTemplateVersionMutations';
import useTemplateMutations from 'routes/FormTemplate/utils/useTemplateMutations';
import useFormTemplateVersionQueryHook from 'utils/fetch/hooks/useFormTemplateVersionQueryHook';
import useFormTemplateQueryHook from 'utils/fetch/hooks/useFormTemplateQueryHook';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import getPreparedTaskScheduleDataFromValues from 'routes/Task/utils/getPreparedTaskScheduleDataFromValues';
import getPreparedFormCategoryDataFromValues from './getPreparedFormCategoryDataFromValues';
import getPreparedFormTemplateDataFromValues, {
  getPreparedFormTemplateVersionDataFromValues,
} from './getPreparedFormTemplateDataFromValues';
import { FormTemplateMode } from '../enums';

interface UseActionsProps {
  formRef: FormRefType;
  id?: string;
  initialDocumentationLinksRef?: React.RefObject<Option[]>;
  isViewLocked?: boolean;
  versionId?: string;
  viewMode: FormTemplateMode;
  useModalManagementResult: ModalManagementResult;
}

export interface UseActionsResults {
  destroyLoading: boolean;
  formCategoryCreateMutationError?: ErrorLike;
  formTemplateArchiveLoading: boolean;
  formTemplateVersionArchiveLoading: boolean;
  formTemplateVersionCreateMutationLoading: boolean;
  formTemplateVersionDestroyLoading: boolean;
  formTemplateVersionUpdateMutationLoading: boolean;
  formCategoryCreateMutationLoading: boolean;
  formTemplateCreateMutationLoading: boolean;
  formTemplateData: Values;
  formTemplateError?: ErrorLike;
  formTemplateGraphQLErrors?: ErrorLike;
  formTemplateFirstLoading: boolean;
  formTemplateLoading: boolean;
  /**
   * Apollo refetch for the form-template query. Exposed so siblings (e.g.
   * `FormTemplateBuilderDocumentsView`) can refresh `hasLinkedDocumentations`
   * after a `formTemplateUnlinkAllDocumentations` mutation succeeds.
   */
  formTemplateRefetch?: () => Promise<unknown>;
  formTemplateUpdateMutationLoading: boolean;
  formTemplateVersionData: Values;
  formTemplateVersionLoading: boolean;
  loadingState: boolean;
  onCancelForm: () => void;
  onCloseScheduleModal: () => void;
  onConfirmedDestroy: () => void;
  onConfirmedVersionDestroy: () => void;
  onCreateNewFormTemplateVersion: () => void;
  onCreateTaskHandler: () => void;
  onDuplicateHandler: () => void;
  onDuplicateVersionHandler: () => void;
  onEditHandler: () => void;
  onEditVersionHandler: () => void;
  onFormCategoryCreate: (values: Values) => void;
  onFormTemplateArchiveHandler: () => void;
  onFormTemplateBulkRestoreHandler: () => void;
  onFormTemplateConfirmedArchive: () => void;
  onFormTemplateVersionArchiveHandler: () => void;
  onFormTemplateVersionConfirmedArchive: () => void;
  onFormTemplateVersionRestoreHandler: () => void;
  onSubmitForm: (values: Values) => void;
  onTaskFormTemplateVersionBulkChange: (
    newFormTemplateVersionId: string,
  ) => void;
  onTaskFormTemplateVersionChange: (
    taskId: string,
    newFormTemplateVersionId: string,
  ) => void;
  onRescheduleHandler: (values?: Values) => void;
  onVersionFieldChangeCallBack: (
    newVersionId?: string,
    newVersionStatus?: string,
  ) => void;
  onViewFormTemplateVersion: (versionIdSrt: string) => void;
  onViewHandler: () => void;
  taskBulkSchedulingError?: ErrorLike;
  taskBulkSchedulingLoading: boolean;
  taskScheduleData?: Task;
  taskScheduleLoading: boolean;
  tasksQueryHook: FormTemplateTasksQueryHookResult;
  useBulkSelectResult: UseBulkSelectResult<Task>;
}

export default function useActions(props: UseActionsProps): UseActionsResults {
  const {
    formRef,
    id = '',
    initialDocumentationLinksRef,
    isViewLocked,
    versionId = '',
    viewMode,
    useModalManagementResult,
  } = props;
  const {
    closeArchiveModal,
    closeCategoryFormModal,
    closeScheduleModal,
    closeVersionArchiveModal,
    closeVersionDestroyModal,
    isScheduleModalOpened,
    openArchiveModal,
    openVersionArchiveModal,
  } = useModalManagementResult;
  const [taskBulkSchedulingLoading, setTaskBulkSchedulingLoading] =
    useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery] = useSearchParam();
  const [filterParams] = useFilterParams();
  const [paginationParams] = usePaginationParams();
  const useLoadingStateResult = useLoadingState();
  const { loadingState, setLoadingState } = useLoadingStateResult;
  const useBulkSelectResult = useTableItemsBulkSelect<Task>();
  const {
    handleSelectSingleTableItem,
    handleUnselectAllTableItems,
    selectedSingleTableItem,
    selectedTableItems,
  } = useBulkSelectResult;

  const duplicatedVersionId = _get(
    filterParams,
    GetUrlParams.DuplicatedVersionId,
    '',
  ) as string;

  // Form Template query
  const useFormTemplateQueryHookResult = useFormTemplateQueryHook({
    id,
    viewMode,
  });
  const {
    data: formTemplateData,
    error: formTemplateError,
    loading: formTemplateLoading,
    firstLoading: formTemplateFirstLoading,
    refetch: formTemplateRefetch,
  } = useFormTemplateQueryHookResult;

  const formTemplateRefetchHandler = () => {
    if (formTemplateRefetch) {
      formTemplateRefetch().finally(() => {
        setLoadingState(false);
      });
    } else {
      setLoadingState(false);
    }
  };

  // Form Template Version
  const formTemplateVersionQueryHook = useFormTemplateVersionQueryHook({
    id,
    versionId,
    duplicatedVersionId,
    isViewLocked,
    viewMode,
  });
  const {
    formTemplateVersion,
    formTemplateVersionData,
    formTemplateVersionDataStatus,
    formTemplateVersionError,
    formTemplateVersionFirstLoading,
    formTemplateVersionLoading,
    formTemplateVersionRefetch,
  } = formTemplateVersionQueryHook;

  // Linked tasks
  const tasksQueryHook = useFormTemplateTasksQueryHook({
    searchQuery,
    paginationParams,
    customScope: {
      linkedToFormTemplateGrouped: { templateId: id },
    },
    skip: !versionId || viewMode !== FormTemplateMode.ViewLinkedTasks,
  });
  const { fetchTaskIds, refetchTasks } = tasksQueryHook;

  let taskScheduleQueryId: string | undefined;
  if (selectedTableItems?.length === 1) {
    taskScheduleQueryId = _get(selectedTableItems, [0, 'id']);
  }
  if (selectedSingleTableItem?.id) {
    taskScheduleQueryId = selectedSingleTableItem.id;
  }
  const skipTaskScheduleQuery = !taskScheduleQueryId || !isScheduleModalOpened;
  const taskScheduleQuery = useDashboardTaskScheduleQuery({
    skip: skipTaskScheduleQuery,
    variables: { id: taskScheduleQueryId || '' },
    fetchPolicy: 'no-cache',
  });
  const { loading: taskScheduleLoading, refetch: taskScheduleRefetch } =
    taskScheduleQuery;
  const taskScheduleData = _get(taskScheduleQuery, 'data.data') as Task;

  const onGetAllTaskIds = () =>
    getAllTableItemsIds<Task>({
      fetchIds: fetchTaskIds,
      useBulkSelectResult,
    });

  const [taskBulkSchedulingMutation, taskBulkSchedulingResult] =
    useTaskBulkSchedulingMutation({
      onCompleted: ({ data: responseData }) => {
        setTaskBulkSchedulingLoading(false);
        if (responseData) {
          toast({ content: t('tasks-frequency-successfully-updated') });
        }
        closeScheduleModal();
        refetchTasks();
        if (!skipTaskScheduleQuery) taskScheduleRefetch();
        handleSelectSingleTableItem(null);
        handleUnselectAllTableItems();
      },
      onError: (error) => {
        setTaskBulkSchedulingLoading(false);
        showGraphQLErrors(error, t);
      },
    });
  const { error: taskBulkSchedulingError } = taskBulkSchedulingResult;

  // Form Template mutations
  const useTemplateMutationsResult = useTemplateMutations({
    formTemplateRefetchHandler,
    formTemplateVersionRefetch,
    useLoadingStateResult,
  });
  const {
    destroyMutation,
    destroyMutationResult,
    formTemplateBulkArchiveMutation,
    formTemplateBulkArchiveMutationResult,
    formTemplateBulkRestoreMutation,
    formTemplateCreateMutation,
    formTemplateCreateMutationResult,
    formTemplateUpdateMutation,
    formTemplateUpdateMutationResult,
  } = useTemplateMutationsResult;

  // Form Template Version mutations
  const useTemplateVersionMutationsResult = useTemplateVersionMutations({
    closeVersionDestroyModal,
    formTemplateRefetch,
    formTemplateRefetchHandler,
    id,
    tasksQueryHook,
    useBulkSelectResult,
    useLoadingStateResult,
    versionId,
  });
  const {
    formTemplateVersionArchiveMutation,
    formTemplateVersionArchiveMutationResult,
    formTemplateVersionCreateMutation,
    formTemplateVersionCreateMutationResult,
    formTemplateVersionDestroyMutation,
    formTemplateVersionDestroyMutationResult,
    formTemplateVersionRestoreMutation,
    formTemplateVersionUpdateMutation,
    formTemplateVersionUpdateMutationResult,
    taskBulkTemplateVersionUpdateMutation,
  } = useTemplateVersionMutationsResult;

  // Form category
  const [formCategoryCreateMutation, formCategoryCreateMutationResult] =
    useFormCategoryCreateMutation({
      onCompleted: (response) => {
        const category = { ...response.data } as FormCategory;
        const categoryId = category?.id;
        const categoryName = category?.name;
        const categoryColor = category?.color;
        if (categoryId && categoryName && formRef?.current?.setValue) {
          formRef.current.setValue(
            'category',
            {
              value: categoryId,
              label: categoryName,
              color: categoryColor,
            },
            { shouldDirty: true, shouldValidate: true },
          );
        }
        closeCategoryFormModal();
        toast({ content: t('create-form-template-category-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  // Actions
  const onFormTemplateCreate = (values: Values) => {
    const attributes = getPreparedFormTemplateDataFromValues({
      values,
      prevValues: formTemplateData as Values,
      t,
      isCreate: true,
    }) as FormTemplateCreateInputObject;
    formTemplateCreateMutation({ variables: { attributes } });
  };

  const onFormTemplateVersionCreate = (values: Values) => {
    const attributes = getPreparedFormTemplateVersionDataFromValues({
      values: removeIdFields(values, ['attachmentsAttached']),
      prevValues: {},
      t,
    });
    formTemplateVersionCreateMutation({
      variables: { attributes, formTemplateId: id },
    });
  };

  const onFormTemplateUpdate = (values: Values) => {
    if (id) {
      const attributes = getPreparedFormTemplateDataFromValues({
        values,
        prevValues: formTemplateData as Values,
        prevDocumentationLinks: initialDocumentationLinksRef?.current,
        t,
      });
      formTemplateUpdateMutation({ variables: { attributes, id } });
    }
  };

  const onFormTemplateVersionUpdate = (values: Values) => {
    if (id) {
      const attributes = getPreparedFormTemplateVersionDataFromValues({
        values,
        prevValues: formTemplateVersionData as Values,
        t,
      });
      formTemplateVersionUpdateMutation({
        variables: {
          attributes,
          id: versionId,
          formTemplateId: id,
        },
      });
    }
  };

  const onFormTemplateDuplicate = (values: Values) => {
    if (id) {
      const attributes = getPreparedFormTemplateDataFromValues({
        isCreate: true,
        prevValues: {},
        t,
        values: removeIdFields(values, ['attachmentsAttached']),
      }) as FormTemplateCreateInputObject;
      formTemplateCreateMutation({ variables: { attributes } });
    }
  };

  const onFormCategoryCreate = (values: Values) => {
    const attributes = getPreparedFormCategoryDataFromValues(values);
    formCategoryCreateMutation({ variables: { attributes } });
  };

  const onDuplicateHandler = () => {
    if (id && versionId) {
      navigate(
        APP_URLS.dashboard.formTemplates.duplicate.getDynamicPath({
          pathParts: { id, versionId },
        }),
      );
    }
  };

  const onDuplicateVersionHandler = () => {
    if (id && versionId) {
      navigate(
        APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
          pathParts: { id, versionId: NEW },
          params: { [GetUrlParams.DuplicatedVersionId]: versionId },
        }),
      );
    }
  };

  const onConfirmedDestroy = () => {
    if (id) {
      destroyMutation({
        variables: { ids: [id] },
      });
    }
  };

  const onConfirmedVersionDestroy = () => {
    if (id) {
      setLoadingState(true);
      formTemplateVersionDestroyMutation({
        variables: { id: versionId, formTemplateId: id },
      });
    }
  };

  const onFormTemplateArchiveHandler = () => {
    openArchiveModal();
  };

  const onFormTemplateVersionArchiveHandler = () => {
    openVersionArchiveModal();
  };

  const onFormTemplateConfirmedArchive = () => {
    if (id && versionId) {
      setLoadingState(true);
      formTemplateBulkArchiveMutation({
        variables: {
          ids: [id],
        },
        onCompleted: () => {
          closeArchiveModal();
        },
      });
    }
  };

  const onFormTemplateVersionConfirmedArchive = () => {
    if (id && versionId) {
      setLoadingState(true);
      formTemplateVersionArchiveMutation({
        variables: {
          id: versionId,
          formTemplateId: id,
        },
        onCompleted: () => {
          closeVersionArchiveModal();
        },
      });
    }
  };

  const onFormTemplateVersionRestoreHandler = () => {
    if (id && versionId) {
      setLoadingState(true);
      formTemplateVersionRestoreMutation({
        variables: {
          id: versionId,
          formTemplateId: id,
        },
      });
    }
  };

  const onFormTemplateBulkRestoreHandler = () => {
    if (id) {
      setLoadingState(true);
      formTemplateBulkRestoreMutation({
        variables: {
          ids: [id],
        },
      });
    }
  };

  const onViewHandler = () => {
    const formTemplateVersionId =
      versionId || _get(formTemplateData, ['currentVersion', 'id']);
    if (id && formTemplateVersionId) {
      navigate(
        APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
          pathParts: {
            id,
            versionId:
              formTemplateVersionId === NEW
                ? (_get(formTemplateData, [
                    FormTemplateFieldNames.CurrentVersion,
                    'id',
                  ]) as string)
                : formTemplateVersionId,
          },
        }),
      );
    }
  };

  const onEditHandler = () => {
    if (id) {
      navigate(
        APP_URLS.dashboard.formTemplates.edit.getDynamicPath({
          pathParts: {
            id,
          },
        }),
      );
    }
  };

  const onEditVersionHandler = () => {
    if (id) {
      navigate(
        APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
          pathParts: {
            id,
            versionId,
          },
        }),
      );
    }
  };

  const onSubmitForm = (values: Values) => {
    if (viewMode === FormTemplateMode.Add) {
      onFormTemplateCreate(values);
    } else if (viewMode === FormTemplateMode.Edit && !versionId) {
      onFormTemplateUpdate(values);
    } else if (viewMode === FormTemplateMode.EditVersion && versionId === NEW) {
      onFormTemplateVersionCreate(values);
    } else if (
      viewMode === FormTemplateMode.EditVersion &&
      versionId &&
      versionId !== NEW
    ) {
      onFormTemplateVersionUpdate(values);
    } else if (viewMode === FormTemplateMode.Duplicate) {
      onFormTemplateDuplicate(values);
    }
  };

  const onCancelForm = () => {
    if (viewMode === FormTemplateMode.Add) {
      navigate(APP_URLS.dashboard.formTemplates.index.getPathWithQuery());
    } else {
      onViewHandler();
    }
  };

  const onCreateTaskHandler = () => {
    navigate(
      APP_URLS.dashboard.tasks.add.getPathWithQuery({
        formTemplateId: formTemplateData.id,
        formTemplateName: formTemplateData.name,
        formTemplateVersionId: formTemplateVersion.id,
        formTemplateVersionName: formTemplateVersion.version,
      }),
    );
  };

  const onViewFormTemplateVersion = (versionIdSrt: string) => {
    navigate(
      APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
        pathParts: {
          id,
          versionId: versionIdSrt,
        },
      }),
    );
  };

  const onVersionFieldChangeCallBack = (
    newVersionId?: string,
    newVersionStatus?: string,
  ) => {
    if (newVersionId === NEW) {
      navigate(
        APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
          pathParts: {
            id,
            versionId: NEW,
          },
        }),
      );
    } else if (newVersionId && viewMode === FormTemplateMode.Duplicate) {
      navigate(
        APP_URLS.dashboard.formTemplates.duplicate.getDynamicPath({
          pathParts: {
            id,
            versionId: newVersionId,
          },
        }),
      );
    } else if (
      newVersionId &&
      (newVersionStatus === FormTemplateVersionStatusEnum.Active ||
        newVersionStatus === FormTemplateVersionStatusEnum.Unused)
    ) {
      navigate(
        APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
          pathParts: {
            id,
            versionId: newVersionId,
          },
        }),
      );
    } else if (
      newVersionId &&
      newVersionStatus === FormTemplateVersionStatusEnum.Archived
    ) {
      navigate(
        APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
          pathParts: {
            id,
            versionId: newVersionId,
          },
        }),
        { replace: true },
      );
    }
  };

  const onCreateNewFormTemplateVersion = () => {
    navigate(
      APP_URLS.dashboard.formTemplates.editVersion.getDynamicPath({
        pathParts: {
          id,
          versionId: NEW,
        },
      }),
    );
  };

  const onTaskFormTemplateVersionChange = (
    taskId: string,
    newFormTemplateVersionId: string,
  ) => {
    setLoadingState(true);
    taskBulkTemplateVersionUpdateMutation({
      variables: {
        input: {
          ids: [taskId],
          templateId: id,
          attributes: { templateVersionId: newFormTemplateVersionId },
        },
      },
    });
  };

  const onTaskFormTemplateVersionBulkChange = (
    newFormTemplateVersionId: string,
  ) => {
    setLoadingState(true);
    tableItemsBulkAction<Task>({
      action: (idsValue?: string[]) => {
        if (idsValue) {
          taskBulkTemplateVersionUpdateMutation({
            variables: {
              input: {
                ids: idsValue,
                templateId: id,
                attributes: { templateVersionId: newFormTemplateVersionId },
              },
            },
          });
        }
      },
      useBulkSelectResult,
      onGetAllIds: () =>
        getAllTableItemsIds<Task>({
          fetchIds: tasksQueryHook.fetchTaskIds,
          useBulkSelectResult,
        }),
    });
  };

  const onRescheduleHandler = (values?: Values) => {
    if (values) {
      setTaskBulkSchedulingLoading(true);
      const action = (ids: string[]) => {
        taskBulkSchedulingMutation({
          variables: {
            ids,
            attributes: getPreparedTaskScheduleDataFromValues(values),
          },
        });
      };
      tableItemsBulkAction<Task>({
        action,
        useBulkSelectResult,
        onGetAllIds: onGetAllTaskIds,
      });
    }
  };

  const onCloseScheduleModal = () => {
    closeScheduleModal();
    taskBulkSchedulingResult.reset();
    handleSelectSingleTableItem(null);
  };

  const formTemplateCreateMutationError =
    formTemplateCreateMutationResult?.error;
  const formTemplateCreateMutationLoading =
    formTemplateCreateMutationResult?.loading;
  const formTemplateVersionCreateMutationError =
    formTemplateVersionCreateMutationResult?.error;
  const formTemplateVersionCreateMutationLoading =
    formTemplateVersionCreateMutationResult?.loading;
  const formTemplateUpdateMutationError =
    formTemplateUpdateMutationResult?.error;
  const formTemplateUpdateMutationLoading =
    formTemplateUpdateMutationResult?.loading;
  const formTemplateVersionUpdateMutationError =
    formTemplateVersionUpdateMutationResult?.error;
  const formTemplateVersionUpdateMutationLoading =
    formTemplateVersionUpdateMutationResult?.loading;

  const {
    error: formCategoryCreateMutationError,
    loading: formCategoryCreateMutationLoading,
  } = formCategoryCreateMutationResult;
  const formTemplateGraphQLErrors =
    formTemplateCreateMutationError ||
    formTemplateUpdateMutationError ||
    formTemplateVersionCreateMutationError ||
    formTemplateVersionUpdateMutationError;

  useEffect(() => {
    if (
      formTemplateVersionDataStatus === FormTemplateVersionStatusEnum.Active &&
      viewMode === FormTemplateMode.Edit
    ) {
      navigate(
        APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
          pathParts: { id, versionId },
        }),
      );
    }
  }, [formTemplateVersionDataStatus, id, navigate, versionId, viewMode]);

  // Guard: a non-editable version (e.g. it has active tasks) opened directly
  // via the edit URL must not render the builder editable — mirror the table
  // menu, which hides "Edit" when canUpdate is false, by redirecting to the
  // read-only view. Skip the "create new version" flow (versionId === NEW),
  // which legitimately reuses EditVersion mode.
  useEffect(() => {
    if (
      viewMode === FormTemplateMode.EditVersion &&
      versionId &&
      versionId !== NEW &&
      _get(formTemplateVersionData, 'canUpdate') === false
    ) {
      navigate(
        APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
          pathParts: { id, versionId },
        }),
        { replace: true },
      );
    }
  }, [formTemplateVersionData, id, navigate, versionId, viewMode]);

  useEffect(() => {
    if (viewMode === FormTemplateMode.View && formTemplateVersionRefetch) {
      formTemplateVersionRefetch();
    }
  }, [formTemplateVersionRefetch, viewMode]);

  return {
    destroyLoading: destroyMutationResult.loading,
    formTemplateArchiveLoading: formTemplateBulkArchiveMutationResult.loading,
    formTemplateVersionArchiveLoading:
      formTemplateVersionArchiveMutationResult.loading,
    formTemplateVersionCreateMutationLoading,
    formTemplateVersionDestroyLoading:
      formTemplateVersionDestroyMutationResult.loading,
    formTemplateVersionUpdateMutationLoading,
    formCategoryCreateMutationError,
    formCategoryCreateMutationLoading,
    formTemplateCreateMutationLoading,
    formTemplateData,
    formTemplateError: formTemplateError || formTemplateVersionError,
    formTemplateGraphQLErrors,
    formTemplateFirstLoading:
      formTemplateFirstLoading || formTemplateVersionFirstLoading,
    formTemplateLoading: formTemplateLoading || formTemplateVersionLoading,
    formTemplateRefetch,
    formTemplateUpdateMutationLoading,
    formTemplateVersionData,
    formTemplateVersionLoading,
    loadingState,
    onCancelForm,
    onCloseScheduleModal,
    onConfirmedDestroy,
    onConfirmedVersionDestroy,
    onCreateNewFormTemplateVersion,
    onCreateTaskHandler,
    onDuplicateHandler,
    onDuplicateVersionHandler,
    onEditHandler,
    onEditVersionHandler,
    onFormCategoryCreate,
    onFormTemplateArchiveHandler,
    onFormTemplateBulkRestoreHandler,
    onFormTemplateConfirmedArchive,
    onFormTemplateVersionArchiveHandler,
    onFormTemplateVersionConfirmedArchive,
    onFormTemplateVersionRestoreHandler,
    onSubmitForm,
    onTaskFormTemplateVersionBulkChange,
    onTaskFormTemplateVersionChange,
    onRescheduleHandler,
    onVersionFieldChangeCallBack,
    onViewFormTemplateVersion,
    onViewHandler,
    taskBulkSchedulingError,
    taskBulkSchedulingLoading,
    taskScheduleData,
    taskScheduleLoading,
    tasksQueryHook,
    useBulkSelectResult,
  };
}
